/**
 * Design tokens do MainSidebar (painel lateral escuro roxo).
 * Referencia: Claude Design "Sidebar.dc.html".
 *
 * Paleta:
 *  superficie   linear-gradient(168deg, #3b2755, #2c1c40 46%, #241634)
 *  borda        #402b5b (externa) / #4a3465 (interna)
 *  hover        #46305f  |  hover forte #563d74
 *  ativo        #4d3568  |  acento #c99cff
 *  texto        #ded4ea (idle) / #f7f3fb (hover-ativo) / #a695b8 (label)
 *  negativo     #f87171 sobre #4d2130
 */

export const sidebarDesign = {
    shell: {
        base: 'flex flex-col flex-shrink-0 h-screen relative z-20 border-r border-[#402b5b] bg-[image:linear-gradient(168deg,#3b2755_0%,#2c1c40_46%,#241634_100%)] transition-[width] duration-300 ease-in-out',
        expanded: 'w-[224px]',
        collapsed: 'w-[58px]',
        mobile: 'w-[238px]',
    },

    header: {
        expanded: 'flex items-center justify-between h-[54px] flex-shrink-0 pl-[18px] pr-3.5',
        collapsed: 'flex items-center justify-center h-[54px] flex-shrink-0',
        wordmark: 'text-[17px] font-extrabold tracking-[0.18em] text-[#f4f0f8]',
        toggle: 'flex items-center justify-center w-[25px] h-[25px] rounded-md text-[#9a8fa8] hover:bg-[#46305f] hover:text-[#f7f3fb] transition-colors',
        logoTile: 'flex items-center justify-center w-[31px] h-[31px] rounded-lg bg-[#46305f] hover:bg-[#563d74] text-[#f7f3fb] text-[11px] font-extrabold tracking-[0.06em] transition-colors',
    },

    flag: 'flex w-full items-center justify-center px-[7px] py-[7px] text-[10.5px] font-semibold text-white',

    // Barra de rolagem oculta: quem indica que ha mais conteudo sao as setas do
    // NavScrollArea. Os tres seletores cobrem Chrome/Edge/Safari (webkit),
    // Firefox (scrollbar-width) e Edge legado (-ms-overflow-style) — a rolagem
    // continua funcionando por roda, teclado e gesto.
    scrollbar:
        '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',

    nav: {
        expanded: 'flex-1 min-h-0 overflow-y-auto px-3 pb-[7px]',
        collapsed: 'flex-1 min-h-0 overflow-y-auto flex flex-col items-center gap-[3px] pt-[9px] pb-[7px]',
        list: 'flex flex-col gap-px',
        groupLabel: 'px-[5px] pt-[7px] pb-[7px] text-[10px] font-extrabold uppercase tracking-[0.06em] text-[#a695b8]',
        divider: 'h-px bg-[#4a3465] mx-[5px] mt-3 mb-[5px]',
        dividerCollapsed: 'w-[22px] h-px bg-[#4a3465] my-[5px] flex-shrink-0',
    },

    // Setas de rolagem do menu (aparecem so quando o conteudo transborda).
    navScroll: {
        wrap: 'relative flex-1 min-h-0 flex flex-col',
        // Faixa que apaga o conteudo por baixo do botao (sem capturar clique).
        fade: 'pointer-events-none absolute inset-x-0 z-[9] h-8',
        fadeTop: 'top-0 bg-[image:linear-gradient(to_bottom,#2c1c40_55%,transparent)]',
        fadeBottom: 'bottom-0 bg-[image:linear-gradient(to_top,#2c1c40_55%,transparent)]',
        // Botao circular com fundo solido, borda clara e sombra: precisa ler
        // como controle clicavel sobre o gradiente escuro da sidebar.
        // Area de hover: o padding ao redor do botao e o "perto do botao" que
        // liga a rolagem automatica. Centralizada e estreita de proposito, para
        // nao roubar o clique do item de menu que fica embaixo.
        zone: 'absolute left-1/2 -translate-x-1/2 z-10 p-2',
        zoneTop: 'top-0',
        zoneBottom: 'bottom-0',
        button: 'flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#5b4179] border border-[#8a68b0] text-[#f7f3fb] shadow-[0_2px_8px_rgba(0,0,0,0.5)] hover:bg-[#7a55a3] hover:border-[#c99cff] active:scale-95 transition-all',
    },

    item: {
        base: 'group relative flex w-full items-center gap-[10px] h-8 px-[10px] rounded-lg text-[11.5px] transition-colors outline-none',
        idle: 'font-semibold text-[#ded4ea] hover:bg-[#46305f] hover:text-[#f7f3fb]',
        active: 'font-bold bg-[#4d3568] text-[#f7f3fb]',
        accent: 'absolute left-0 top-[7px] bottom-[7px] w-[3px] rounded-full bg-[#c99cff]',
        label: 'flex-1 truncate text-left',
        icon: 'flex-shrink-0 transition-colors',
        iconIdle: 'text-[#c0b2d2] group-hover:text-[#f7f3fb]',
        iconActive: 'text-[#c99cff]',
        chevron: 'flex-shrink-0 text-[#a695b8] transition-transform duration-200',
    },

    itemCollapsed: {
        base: 'group relative flex items-center justify-center w-[34px] h-[34px] rounded-lg transition-colors outline-none',
        idle: 'text-[#ded4ea] hover:bg-[#46305f] hover:text-[#f7f3fb]',
        active: 'bg-[#4d3568] text-[#c99cff]',
    },

    subItem: {
        rail: 'flex flex-col gap-px mt-0.5 mb-[3px] ml-[20px] pl-[11px] border-l border-[#4a3465]',
        base: 'group relative flex w-full items-center h-[28px] px-[9px] rounded-md text-[11px] font-semibold transition-colors outline-none',
        idle: 'text-[#c8bad9] hover:bg-[#46305f] hover:text-[#f7f3fb]',
        active: 'bg-[#46305f] text-[#f7f3fb]',
    },

    // Submenu lateral quando a sidebar esta recolhida (Popover ao lado do icone).
    flyout: {
        paper: 'ml-[7px] rounded-xl border border-[#4a3465] bg-[#2c1c40] shadow-xl overflow-hidden min-w-[189px]',
        header: 'px-[11px] py-[9px] text-[10px] font-extrabold uppercase tracking-[0.06em] text-[#a695b8] border-b border-[#4a3465]',
        list: 'flex flex-col gap-px p-[5px]',
    },

    badge: {
        base: 'min-w-[18px] px-[5px] py-px rounded-full text-[10px] font-extrabold text-center tabular-nums',
        primary: 'bg-[#b57cf6] text-[#1c1029]',
        neutral: 'bg-[#4a3465] text-[#ded4ea]',
        danger: 'bg-[#4d2130] text-[#f87171]',
        dot: 'absolute top-[5px] right-[5px] w-[6px] h-[6px] rounded-full bg-[#a855f7] shadow-[0_0_0_2px_#2e1d43]',
        newTag: 'flex-shrink-0 px-[5px] py-px rounded text-[8px] font-extrabold uppercase tracking-wide bg-[#b57cf6] text-[#1c1029]',
    },

    footer: {
        expanded: 'flex-shrink-0 px-3 pt-[11px] pb-3 border-t border-[#4a3465]',
        collapsed: 'flex-shrink-0 flex flex-col items-center gap-[7px] px-[7px] pt-[11px] pb-3 border-t border-[#4a3465]',

        credits: 'relative flex w-full items-center gap-[10px] h-9 pl-3 pr-[7px] rounded-lg overflow-hidden hover:bg-[#46305f] transition-colors',
        creditsAccent: 'absolute left-0 top-[7px] bottom-[7px] w-[3px] rounded-full',
        creditsValue: 'text-[11.5px] font-extrabold tabular-nums',
        creditsLabel: 'text-[10.5px] font-semibold text-[#a695b8]',
        creditsRefresh: 'flex items-center justify-center flex-shrink-0 w-[25px] h-[25px] rounded-md text-[#ded4ea] hover:bg-[#563d74] hover:text-[#f7f3fb] transition-colors disabled:opacity-50',
        creditsChip: 'flex flex-col items-center justify-center w-10 py-[4px] rounded-lg transition-colors',
        creditsChipValue: 'text-[11px] font-extrabold tabular-nums',

        actions: 'flex items-center gap-[3px] mt-[9px]',
        settings: 'flex items-center gap-[9px] flex-1 min-w-0 h-[31px] px-[10px] rounded-lg text-[11px] font-semibold transition-colors',
        settingsIdle: 'text-[#ded4ea] hover:bg-[#46305f] hover:text-[#f7f3fb]',
        settingsActive: 'bg-[#4d3568] text-[#f7f3fb]',
        iconAction: 'relative flex items-center justify-center w-[31px] h-[31px] rounded-lg text-[#ded4ea] hover:bg-[#46305f] hover:text-[#f7f3fb] transition-colors',
        // normaliza os gatilhos claros de NotificationsPopover / HelpComponent no tema escuro
        iconSlot: '[&_button]:!h-[31px] [&_button]:!w-[31px] [&_button]:!rounded-lg [&_button]:!text-[#ded4ea] [&_button:hover]:!bg-[#46305f] [&_button:hover]:!text-[#f7f3fb]',
        account: 'mt-[9px]',
    },
};

/** Cores negativas / neutras dos creditos. */
export const creditsTone = (isNegative) => ({
    accent: isNegative ? 'bg-[#f87171]' : 'bg-[#c99cff]',
    value: isNegative ? 'text-[#f87171]' : 'text-[#f4f0f8]',
    chip: isNegative ? 'bg-[#4d2130] text-[#f87171]' : 'bg-[#46305f] text-[#ded4ea]',
});

/** Formata o saldo compacto usado na sidebar colapsada: -2911 -> "-2,9k". */
export const formatCreditsCompact = (value) => {
    const num = Number(value) || 0;
    const abs = Math.abs(num);
    if (abs < 1000) return String(Math.round(num));
    return `${(num / 1000).toFixed(1).replace('.', ',')}k`;
};

/** Formata o saldo completo: -2911 -> "-2.911". */
export const formatCredits = (value) =>
    (Number(value) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

export default sidebarDesign;
